import { newToast } from '../components';
import useApi from '../utils/axios-pettitions';

// Tipos para las funciones
type SetLoadingFunction = (loading: boolean) => void;
type OnSuccessCallback<T = any> = (data?: T) => void;

const Index = () => {
  const { runApi } = useApi();

  // Función helper para formatear fechas
  const formatDateForAPI = (date: Date | null | undefined): string | null => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString().split('T')[0];
  };

  /**
   * Obtiene remisiones con filtros dinámicos
   */
  const getRemisiones = (fechaInicio: Date, fechaFin: Date, estado?: number | null, laboratorioId?: number | null, usuarioId?: number | null): Promise<any[] | null> => {
    const fechaInicioStr = formatDateForAPI(fechaInicio);
    const fechaFinStr = formatDateForAPI(fechaFin);

    if (!fechaInicioStr || !fechaFinStr) {
      console.error('Las fechas deben ser objetos Date válidos');
      newToast('ERROR', 'Fechas inválidas', 4);
      return Promise.resolve(null);
    }

    const params = new URLSearchParams({
      fechaInicio: fechaInicioStr,
      fechaFin: fechaFinStr,
    });

    if (estado != null) params.append('estado', estado.toString());
    if (laboratorioId != null) params.append('laboratorioId', laboratorioId.toString());
    if (usuarioId != null) params.append('usuarioId', usuarioId.toString());

    return runApi(`Remission/GetRemisiones?${params.toString()}`, 'GET', null, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al obtener remisiones', 4);
          return null;
        } else if (res.internalCode === 101) {
          return res.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error getRemisiones:', err);
        newToast('ERROR', 'Error de conexión', 4);
        return null;
      });
  };

  /**
   * Obtiene una remisión por ID
   */
  const getRemisionById = (id: number): Promise<any | null> => {
    return runApi(`Remission/GetRemision`, 'GET', { id }, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al obtener remisión', 4);
          return null;
        } else if (res.internalCode === 101) {
          return res.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error getRemisionById:', err);
        newToast('ERROR', 'Error de conexión', 4);
        return null;
      });
  };

  /**
   * Crea una nueva remisión
   */
  const createRemision = (data: any, setLoading: SetLoadingFunction, onSuccess?: OnSuccessCallback): Promise<any | null> => {
    return runApi('Remission/CreateRemision', 'POST', null, data)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al crear remisión', 4);
          setLoading(false);
          return null;
        } else if (res.internalCode === 105) {
          newToast('SUCCESS', res.message || 'Remisión creada exitosamente', 4);
          if (onSuccess) onSuccess(res.data);
          setLoading(false);
          return res.data;
        }
        setLoading(false);
        return null;
      })
      .catch((err) => {
        console.error('Error createRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        setLoading(false);
        return null;
      });
  };

  /**
   * Actualiza una remisión existente
   */
  const updateRemision = (data: any, setLoading: SetLoadingFunction, onSuccess?: OnSuccessCallback): Promise<any | null> => {
    return runApi('Remission/UpdateRemision', 'POST', null, data)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al actualizar remisión', 4);
          setLoading(false);
          return null;
        } else if (res.internalCode === 107) {
          newToast('SUCCESS', res.message || 'Remisión actualizada exitosamente', 4);
          if (onSuccess) onSuccess(res.data);
          setLoading(false);
          return res.data;
        }
        setLoading(false);
        return null;
      })
      .catch((err) => {
        console.error('Error updateRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        setLoading(false);
        return null;
      });
  };

  /**
   * Cambia el estado de una remisión
   */
  const changeEstadoRemision = (id: number, nuevoEstado: number, setLoading: SetLoadingFunction, onSuccess?: OnSuccessCallback): Promise<boolean> => {
    const requestData = { Id: id, NuevoEstado: nuevoEstado };

    return runApi('Remission/ChangeEstado', 'POST', null, requestData)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al cambiar estado', 4);
          setLoading(false);
          return false;
        } else if (res.internalCode === 106) {
          newToast('SUCCESS', res.message || 'Estado actualizado exitosamente', 4);
          if (onSuccess) onSuccess();
          setLoading(false);
          return true;
        }
        setLoading(false);
        return false;
      })
      .catch((err) => {
        console.error('Error changeEstadoRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        setLoading(false);
        return false;
      });
  };

  /**
   * Elimina una remisión
   */
  const deleteRemision = (id: number, setLoading: SetLoadingFunction, onSuccess?: OnSuccessCallback): Promise<boolean> => {
    const requestData = { Id: id };

    return runApi('Remission/DeleteRemision', 'POST', null, requestData)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al eliminar remisión', 4);
          setLoading(false);
          return false;
        } else if (res.internalCode === 107) {
          newToast('SUCCESS', res.message || 'Remisión eliminada exitosamente', 4);
          if (onSuccess) onSuccess();
          setLoading(false);
          return true;
        }
        setLoading(false);
        return false;
      })
      .catch((err) => {
        console.error('Error deleteRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        setLoading(false);
        return false;
      });
  };

  //Obtiene órdenes y estudios pendientes de remisión para un laboratorio específico
  const getOrdenesPendientesRemision = (laboratorioId: number, fechaInicio?: Date | null, fechaFin?: Date | null) => {
    const fechaInicioStr = formatDateForAPI(fechaInicio);
    const fechaFinStr = formatDateForAPI(fechaFin);

    return runApi('Remission/GetOrdenesPendientes', 'GET', { laboratorioId: laboratorioId, fechaInicio: fechaInicioStr, fechaFin: fechaFinStr }, null).then((res) => {
      if (res.internalCode === 101) {
        return res.data || [];
      }
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message || 'Error al obtener órdenes pendientes', 4);
        return null;
      }
      console.log('Error en la consulta de órdenes pendientes de remisión', res);
      return null;
    });
  };

  //Obtiene estudios disponibles para remisión de un laboratorio específico
  const getEstudiosDisponiblesRemision = (laboratorioId: number) => {
    return runApi('Remission/GetEstudiosDisponibles', 'GET', { laboratorioId: laboratorioId }, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al obtener estudios disponibles', 4);
          return null;
        } else if (res.internalCode === 101) {
          return res.data || [];
        }
        return [];
      })
      .catch((err) => {
        console.error('Error getEstudiosDisponiblesRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        return null;
      });
  };

  //Obtiene el detalle de una remisión
  const getDetalleRemision = (remisionId: number) => {
    return runApi('Remission/GetDetalle', 'GET', { remisionId: remisionId }, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al obtener detalle', 4);
          return null;
        } else if (res.internalCode === 101) {
          return res.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error getDetalleRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        return null;
      });
  };

  //Agrega un estudio a una remisión
  const addDetalleRemision = (data: any, setLoading: SetLoadingFunction, onSuccess?: OnSuccessCallback) => {
    return runApi('Remission/AddDetalle', 'POST', null, data)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al agregar estudio', 4);
          setLoading(false);
          return null;
        } else if (res.internalCode === 105) {
          newToast('SUCCESS', res.message || 'Estudio agregado exitosamente', 4);
          if (onSuccess) onSuccess(res.data);
          setLoading(false);
          return res.data;
        }
        setLoading(false);
        return null;
      })
      .catch((err) => {
        console.error('Error addDetalleRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        setLoading(false);
        return null;
      });
  };

  //Remueve un estudio de una remisión
  const removeDetalleRemision = (detalleId: number, setLoading: SetLoadingFunction, onSuccess?: OnSuccessCallback) => {
    const requestData = { Id: detalleId };

    return runApi('Remission/RemoveDetalle', 'POST', null, requestData)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al remover estudio', 4);
          setLoading(false);
          return false;
        } else if (res.internalCode === 107) {
          newToast('SUCCESS', res.message || 'Estudio removido exitosamente', 4);
          if (onSuccess) onSuccess();
          setLoading(false);
          return true;
        }
        setLoading(false);
        return false;
      })
      .catch((err) => {
        console.error('Error removeDetalleRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        setLoading(false);
        return false;
      });
  };

  //Actualiza el estado de recepción de resultado
  const updateResultadoRecibido = (detalleId: number, resultadoRecibido: boolean, setLoading: SetLoadingFunction, onSuccess?: OnSuccessCallback) => {
    const requestData = { DetalleId: detalleId, ResultadoRecibido: resultadoRecibido };

    return runApi('Remission/UpdateResultado', 'POST', null, requestData)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al actualizar resultado', 4);
          setLoading(false);
          return false;
        } else if (res.internalCode === 107) {
          newToast('SUCCESS', res.message || 'Resultado actualizado exitosamente', 4);
          if (onSuccess) onSuccess();
          setLoading(false);
          return true;
        }
        setLoading(false);
        return false;
      })
      .catch((err) => {
        console.error('Error updateResultadoRecibido:', err);
        newToast('ERROR', 'Error de conexión', 4);
        setLoading(false);
        return false;
      });
  };

  //Obtiene laboratorios activos
  const getLaboratorios = () => {
    return runApi('Remission/GetLaboratorios', 'GET', null, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al obtener laboratorios', 4);
          return null;
        } else if (res.internalCode === 101) {
          return res.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error getLaboratorios:', err);
        newToast('ERROR', 'Error de conexión', 4);
        return null;
      });
  };

  //Obtiene un laboratorio por ID
  const getLaboratorioById = (id: number) => {
    return runApi(`Remission/GetLaboratorio/${id}`, 'GET', null, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al obtener laboratorio', 4);
          return null;
        } else if (res.internalCode === 101) {
          return res.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error getLaboratorioById:', err);
        newToast('ERROR', 'Error de conexión', 4);
        return null;
      });
  };

  //Verifica si un estudio existe en una remisión activa
  const existsEstudioInRemision = (ordenId: number, estudioId: number) => {
    const params = new URLSearchParams({
      ordenId: ordenId.toString(),
      estudioId: estudioId.toString(),
    });

    return runApi(`Remission/ExistsEstudio?${params.toString()}`, 'GET', null, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          return false;
        } else if (res.internalCode === 101) {
          return res.data.exists;
        }
        return false;
      })
      .catch((err) => {
        console.error('Error existsEstudioInRemision:', err);
        return false;
      });
  };

  //Obtiene estudios pendientes de resultado
  const getEstudiosPendientesResultado = () => {
    return runApi('Remission/GetPendientesResultado', 'GET', null, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al obtener estudios pendientes', 4);
          return null;
        } else if (res.internalCode === 101) {
          return res.data;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error getEstudiosPendientesResultado:', err);
        newToast('ERROR', 'Error de conexión', 4);
        return null;
      });
  };

  //Obtiene contador de remisiones por laboratorio
  const getContadorRemisiones = (laboratorioId: number, fechaInicio: Date, fechaFin: Date) => {
    const fechaInicioStr = formatDateForAPI(fechaInicio);
    const fechaFinStr = formatDateForAPI(fechaFin);

    if (!fechaInicioStr || !fechaFinStr) {
      console.error('Las fechas deben ser objetos Date válidos');
      newToast('ERROR', 'Fechas inválidas', 4);
      return Promise.resolve(null);
    }

    const params = new URLSearchParams({
      laboratorioId: laboratorioId.toString(),
      fechaInicio: fechaInicioStr,
      fechaFin: fechaFinStr,
    });

    return runApi(`Remission/GetContador?${params.toString()}`, 'GET', null, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al obtener contador', 4);
          return null;
        } else if (res.internalCode === 101) {
          return res.data.count;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error getContadorRemisiones:', err);
        newToast('ERROR', 'Error de conexión', 4);
        return null;
      });
  };

  //Obtiene remisiones temporales activas
  const getTemporalRemisions = (IdEst?: number) => {
    return runApi('Remission/GetTemporalRemisions', 'GET', null, null)
      .then((res) => {
        if (res.internalCode >= 200) {
          return null;
        } else if (res.internalCode === 101) {
          // Verificar si hay registros
          if (res.data && res.data.length > 0) {
            if (IdEst) {
              // Verificar si el estudio está dentro de la lista
              const dataresp = res.data.filter((x: any) => x.Rmt_Es_Id === IdEst);
              if (dataresp.length > 0) {
                return dataresp;
              }
              return null;
            }
            return res.data;
          }
          return null;
        }
        return null;
      })
      .catch((err) => {
        console.error('Error getTemporalRemisions:', err);
        return null;
      });
  };

  //Crea una nueva remisión temporal
  const CreateTemporalRemision = (data: any) => {
    return runApi('Remission/createTemporalRemision', 'POST', null, data)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al crear remisión temporal', 4);
          return res.internalCode;
        } else if (res.internalCode === 105) {
          newToast('SUCCESS', res.message || 'Remisión temporal creada exitosamente', 4);
          return res.internalCode;
        }
        return res.internalCode;
      })
      .catch((err) => {
        console.error('Error createTemporalRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        throw err;
      });
  };

  //Actualiza una remisión temporal
  const SetTemporalRemision = (data: any) => {
    return runApi('Remission/SetTemporalRemision', 'POST', null, data)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al actualizar remisión temporal', 4);
          return res.internalCode;
        } else if (res.internalCode === 107) {
          newToast('SUCCESS', res.message || 'Remisión temporal actualizada exitosamente', 4);
          return res.internalCode;
        }
        return res.internalCode;
      })
      .catch((err) => {
        console.error('Error setTemporalRemision:', err);
        newToast('ERROR', 'Error de conexión', 4);
        throw err;
      });
  };

  const DeleteTemporalRemision = (id: number) => {
    return runApi('Remission/DeleteTemporalRemision', 'POST', { id: id }, null).then((res) => {
      if (res.internalCode >= 200) {
        newToast('ERROR', res.message || 'Error al eliminar remisión temporal', 4);
        return false;
      }
    });
  };

    const UpdateLabRefStudy = (idEs: number, idLabRef: number| null) => {      
    return runApi('Remission/UpdateLabRefStudy', 'POST', { idEs: idEs, idLabRef: idLabRef }, null).then((res) => {
      if (res.internalCode >= 200) {        
        return false;
      }
      return true;
    });
  };

  //Actualiza una remisión completa (encabezado y detalles)
  const updateRemisionCompleta = (data: any, setLoading: SetLoadingFunction, onSuccess?: OnSuccessCallback) => {
    return runApi('Remission/UpdateRemisionCompleta', 'POST', null, data)
      .then((res) => {
        if (res.internalCode >= 200) {
          newToast('ERROR', res.message || 'Error al actualizar remisión', 4);
          setLoading(false);
          return null;
        } else if (res.internalCode === 107) {
          newToast('SUCCESS', res.message || 'Remisión actualizada exitosamente', 4);
          if (onSuccess) onSuccess(res.data);
          setLoading(false);
          return res.data;
        }
        setLoading(false);
        return null;
      })
      .catch((err) => {
        console.error('Error updateRemisionCompleta:', err);
        newToast('ERROR', 'Error de conexión', 4);
        setLoading(false);
        return null;
      });
  };

  return {
    // Remisiones principales
    getRemisiones,
    getRemisionById,
    createRemision,
    updateRemision,
    updateRemisionCompleta,
    changeEstadoRemision,
    deleteRemision,

    // Detalle de remisiones
    getDetalleRemision,
    addDetalleRemision,
    removeDetalleRemision,
    updateResultadoRecibido,

    // Laboratorios
    getLaboratorios,
    getLaboratorioById,

    // Consultas especializadas
    existsEstudioInRemision,
    getEstudiosPendientesResultado,
    getContadorRemisiones,

    // Nuevos métodos para órdenes pendientes
    getOrdenesPendientesRemision,
    getEstudiosDisponiblesRemision,

    // Remisiones temporales (existentes)
    getTemporalRemisions,
    CreateTemporalRemision,
    SetTemporalRemision,
    DeleteTemporalRemision,

    // Actualización de laboratorio de referencia en estudio (JOSE)
    UpdateLabRefStudy,
  };
};

export default Index;
